import React, { useState, useEffect } from "react";
import "./App.css";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaMapMarkerAlt,
  FaSearch,
  FaBars,
  FaTimes,
  FaUserMd,
  FaHeartbeat,
  FaStethoscope,
  FaFlask
} from "react-icons/fa";
import { Helmet } from "react-helmet-async"; // ✅ Added

const About = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [counts, setCounts] = useState({
    experience: 0,
    patients: 0,
    doctors: 0,
    departments: 0,
  });

  // ===== Scroll header effect =====
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ===== Prevent body scroll when menu is open =====
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  // ===== Counter animation =====
  useEffect(() => {
    const target = {
      experience: 15,
      patients: 50000,
      doctors: 120,
      departments: 25,
    };

    const duration = 2000; // 2 seconds
    const steps = 60;
    let currentStep = 0;

    const counterInterval = setInterval(() => {
      currentStep++;
      setCounts({
        experience: Math.floor((target.experience / steps) * currentStep),
        patients: Math.floor((target.patients / steps) * currentStep),
        doctors: Math.floor((target.doctors / steps) * currentStep),
        departments: Math.floor((target.departments / steps) * currentStep),
      });

      if (currentStep >= steps) clearInterval(counterInterval);
    }, duration / steps);
  }, []);

  return (
    <div className="about-page">
      {/* ✅ SEO Metadata for About Page */}
      <Helmet>
        <title>About Us | Ashtavinayak Children's Hospital & Critical Care</title>
        <meta
          name="description"
          content="Learn more about Ashtavinayak Children's Hospital & Critical Care, Sambhajinagar — our mission, expertise, and dedication to providing world-class child and maternal healthcare."
        />
        <meta
          name="keywords"
          content="About Ashtavinayak Hospital, Children's Hospital Sambhajinagar, Pediatric Hospital, Neonatal Care, Maternity Hospital, Doctors, NICU, PICU"
        />
        <meta name="author" content="Ashtavinayak Children's Hospital & Critical Care" />

        {/* Open Graph / Facebook */}
        <meta property="og:title" content="About Ashtavinayak Children's Hospital & Critical Care" />
        <meta property="og:description" content="Discover the story, mission, and excellence behind Ashtavinayak Children's Hospital — caring for families with compassion and innovation." />
        <meta property="og:image" content="/logo.png" />
        <meta property="og:url" content="https://www.ashtavinayakhospital.in/about" />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Ashtavinayak Children's Hospital & Critical Care" />
        <meta name="twitter:description" content="Dedicated to providing exceptional child and maternal healthcare in Sambhajinagar." />
        <meta name="twitter:image" content="/logo.png" />

        <link rel="canonical" href="https://www.ashtavinayakhospital.in/about" />
        <meta name="theme-color" content="#0091b8" />
      </Helmet>

      {/* ===== Header ===== */}
      <header
        className={`main-header ${isScrolled ? "scrolled" : ""}`}
        style={{
          zIndex: 5000,
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
        }}
      >
        <div className="navbar-container">
          <a href="/" className="navbar-logo">
            <img
              src="/logo.png"
              alt="Ashtavinayak Children's Hospital & Critical Care"
            />
          </a>

          <nav
            className={`nav-links ${menuOpen ? "active" : ""}`}
            style={{
              zIndex: 6000,
              position: menuOpen ? "fixed" : "",
            }}
          >
            <a href="/" onClick={() => setMenuOpen(false)}>Home</a>
            <a href="/about" onClick={() => setMenuOpen(false)}>About Us</a>
            <a href="/facilities" onClick={() => setMenuOpen(false)}>Facilities</a>
            <a href="/contact" onClick={() => setMenuOpen(false)}>Contact</a>
          </nav>

          <button
            className="menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle Menu"
          >
            {menuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      {/* ===== Hero Section ===== */}
      <section className="about-hero">
        <div className="hero-overlay">
          <div className="about-hero-content">
            <h1>About Ashtavinayak Children's Hospital & Critical Care</h1>
            <p>
              Providing exceptional child healthcare with compassion,
              innovation, and trust — shaping brighter, healthier beginnings for every family.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Nurturing Section ===== */}
      <section className="nurturing-section">
        <div className="nurturing-container">
          <div className="nurturing-image-frame">
            <img
              src="/ashtavinayakhospital_aboutbanner.png"
              alt="Baby care"
              className="nurturing-image bordered-image"
            />
          </div>

          <div className="nurturing-content">
            <h2>
              Nurturing New Beginnings <br />
              Leading the Way in <br />
              Child Health <br />
              Compassionate Care for <br />
              Growing Hearts
            </h2>
            <p>
              At <strong>Ashtavinayak Children's Hospital & Critical Care</strong>, we are more than just a healthcare facility;
              we are a family of dedicated professionals driven by compassion and excellence.
            </p>
            <p>
              Located in Sambhajinagar, our hospital stands as a symbol of hope and advanced medical
              care for children and mothers, offering personalized treatment that makes a difference.
            </p>
            <button className="contact-btn">Contact Us ›</button>
          </div>
        </div>
      </section>

      {/* ===== Counter Section ===== */}
      <section className="counter-section">
        <div className="counter-overlay">
          <div className="counter-container">
            <div className="counter-box">
              <FaUserMd className="counter-icon" />
              <h2>{counts.experience}+</h2>
              <p>Years of Experience</p>
            </div>
            <div className="counter-box">
              <FaHeartbeat className="counter-icon" />
              <h2>{counts.patients.toLocaleString()}+</h2>
              <p>Happy Patients</p>
            </div>
            <div className="counter-box">
              <FaStethoscope className="counter-icon" />
              <h2>{counts.doctors}+</h2>
              <p>Qualified Doctors</p>
            </div>
            <div className="counter-box">
              <FaFlask className="counter-icon" />
              <h2>{counts.departments}+</h2>
              <p>Departments</p>
            </div>
          </div>
        </div>
      </section>

      <a href="tel:+918149492473" className="sticky-call-btn">
        <FaPhoneAlt className="call-icon" />
        <span>Call Us Now</span>
      </a>

      {/* ===== Footer ===== */}
      <footer className="footer-section">
        <div className="footer-container">
          <div className="footer-about">
            <img
              src="/logo.png"
              alt="Ashtavinayak Children's Hospital & Critical Care"
              className="footer-logo"
            />
            <p>
              Ashtavinayak Children's Hospital & Critical Care is dedicated to
              providing comprehensive medical care for children.
            </p>
          </div>

          <div className="footer-links">
            <h4>Quick Links</h4>
            <ul>
              <li><a href="/">Home</a></li>
              <li><a href="/about">About Us</a></li>
              <li><a href="/facilities">Facilities</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>

          <div className="footer-contact">
            <h4>Contact Us</h4>
            <p>Ashtvinayak hospital, KT complex, beside savitri lawns, Sambhajinagar, India-431002</p>
            <p>
              <a href="tel:+918149492473" className="contact-link">
                <FaPhoneAlt /> +91 8149492473
              </a>
            </p>
            <p>
              <a href="mailto:ashtavinayakhospital@gmail.com" className="contact-link">
                <FaEnvelope /> ashtavinayakhospital@gmail.com
              </a>
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>
            © {new Date().getFullYear()} Ashtavinayak Children's Hospital & Critical
            Care. All Rights Reserved.
            <br />
            <span className="developer-text">
              Designed & Developed by{" "}
              <a
                href="https://intelligicsoftware.in"
                target="_blank"
                rel="noopener noreferrer"
              >
                Intellgic Software Pvt. Ltd.
              </a>
            </span>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default About;
